import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { intlShape } from "@ai360/core/intl";
import {
    Checkbox,
    DateInput,
    DialogBox,
    DialogBoxFooterType,
    Loader,
    Pane,
    SelectInput,
    SelectInputMulti,
    Tabs,
    TextInput,
} from "~/core";
import { messages } from "../i18n-messages";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import classNames from "classnames";

import { AgvanceAPI, SkyAPI } from "@ai360/core";

import { AgvanceType } from "../rec-list/components/rec-accordion/rec-accordion-item-context-menu";
import { IOption } from "~/core/components/select-input/model";
import { RecDetails, RecNutrientProduct, RecSummary } from "~/recs-events/recs/model";
import { ProductMixProduct } from "~/action-panel/components/common/product-blend-modal/model";
import { models } from "~/recs-events";

import * as blending from "../../../common/product-blend-modal/blending-utils";
import { getAgvanceLocationsList } from "~/action-panel/components/field-module/selectors";
import { actions as fieldActions } from "~/action-panel/components/field-module/";
import {
    useLoginUserPreferences,
    useUpdateLoginUserPreferences,
} from "~/core/components/login-user-preferences";
import { AgvanceUtils } from "~/admin/setup/customer/agvance-utils";
import { getTheUserPersonalityId } from "~/login/selectors";
import { getProductBlendPicklists } from "~/action-panel/components/common/product-blend-modal/selectors";
import {
    getSendRecToAgvanceBlendResults,
    getSendRecToAgvancePlanResults,
    getSendRecToAgvanceRecStatus,
} from "~/action-panel/components/rec-module/components/rec-info/selectors";
import { getAgvanceRecLoading, getRecDetailsAgvance } from "~/recs-events/recs/selectors";
import { clearRecDetails, fetchNonMapRecDetails } from "~/recs-events/actions";
import { resetApiResult } from "~/core/api/actions";
import { resetSend } from "../rec-info/actions";

import "./send-rec-agvance.css";
import { useIntl } from "react-intl";

interface ISendRecAgvanceProps {
    agvanceType: AgvanceType;
    isOpen: boolean;
    intl: intlShape;
    onSend: (evt: { workOrder: string; instructions: string }) => void;
    onClose: () => void;
    onApiError: (err, message) => void;
    recSummary: RecSummary;
    userGuid: string;
}

const SEND_RECS = "SendRecs";
const DROPDOWN_DATA = "dropdownData";

enum StatusMessage {
    REQUEST_SENT = "Sending Rec to Agvance...",
    VALIDATE_PASSWORD = "Validating Password...",
    SUCCESS = "{0} created: {1}", // 0 = Plan/Blend, 1 = blend ticket / plan letter
    ERROR = "Error:\n{0}",
    ERRORS = "Errors:",
    REQUEST_FAILED = "Failed to send Rec.",
    VALIDATION = "Validation Results:",
    WARNING = "Warnings:",
}
enum NutrientName {
    P = "P2O5",
    K = "K2O",
}
interface ITabInfo {
    include?: boolean;
    prodMixId: string;
    prodMixName: string;
    products: AgvanceAPI.IRecProduct[];
    tabLabel: string;
}

const appendLine = (ref: React.MutableRefObject<string>, line: string, tabs?: number) => {
    const prefixedLine = tabs != null && tabs > 0 ? `${"\t".repeat(tabs)}${line}` : line;
    ref.current = ref.current == null ? prefixedLine : `${ref.current}\n${prefixedLine}`;
};

const SendRecAgvance = (props: ISendRecAgvanceProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const recGeneralDetails: RecDetails = useAppSelector(getRecDetailsAgvance);
    const personalityId = useAppSelector(getTheUserPersonalityId);

    const productBlendPicklists = useAppSelector(getProductBlendPicklists);
    const locationList = useAppSelector(getAgvanceLocationsList) as AgvanceAPI.ILocationResponse[];
    const isRecLoading = useAppSelector(getAgvanceRecLoading) as boolean;

    const blendResults = useAppSelector(
        getSendRecToAgvanceBlendResults
    ) as AgvanceAPI.IBlendResultMessage[];
    const planResults = useAppSelector(
        getSendRecToAgvancePlanResults
    ) as AgvanceAPI.IPlanResultMessage[];
    const recStatus = useAppSelector(
        getSendRecToAgvanceRecStatus
    ) as AgvanceAPI.IBlendPlanResultStatusMessage;

    const [successfulBlendPlan, setSuccessfulBlendPlan] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [disableSend, setDisableSend] = useState<boolean>(true);
    const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
    const [showValidationWarningDialog, setShowValidationWarningDialog] = useState<boolean>(false);
    const [showValidationPasswordDialog, setShowValidationPasswordDialog] =
        useState<boolean>(false);
    const [creditPassword, setCreditPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [validatedRequestKey, setValidatedRequestKey] = useState<string>(null);
    const blendSetType = useRef<string>(null);

    const [allProductsInTable, setAllProductsInTable] = useState<AgvanceAPI.IRecProduct[]>([]);
    const [customApplied, setCustomApplied] = useState<boolean>(false);
    const [productMixProdList, setProductMixProdList] = useState<ITabInfo[]>([]);
    const [isSinglePlanOrTicket, setIsSinglePlanOrTicket] = useState<boolean>(false);
    const [agvanceCrop, setAgvanceCrop] = useState<string>(null);
    const [agvanceChemistry, setAgvanceChemistry] = useState<string>(null);
    const [agvancePreferences, setAgvancePreferences] = useState<AgvanceAPI.IAgvancePreference[]>(
        []
    );
    const [targetPests, setTargetPests] = useState<string[]>(null);
    const [targetPestsList, setTargetPestsList] = useState<AgvanceAPI.ITargetPest[]>([]);
    const [showTargetPests, setShowTargetPests] = useState<boolean>(false);
    const [showStartDate, setShowStartDate] = useState<boolean>(false);
    const [showEndDate, setShowEndDate] = useState<boolean>(false);
    const [planStartDate, setPlanStartDate] = useState<moment.Moment | string>(null);
    const [planEndDate, setPlanEndDate] = useState<moment.Moment | string>(null);
    const [selectedFederalPermit, setSelectedFederalPermit] = useState<string>(null);
    const [selectedStatePermits, setSelectedStatePermits] = useState<string[]>([]);
    const [showFederalPermits, setShowFederalPermits] = useState<boolean>(false);
    const [showStatePermits, setShowStatePermits] = useState<boolean>(false);

    const nutrientsIncludePreference = useRef<Record<string, boolean>>({});
    const customerGuid = useRef<string>(props.recSummary?.customerGuid);
    const stopWithPassword = useRef<boolean>(false);

    const [planTicketRequest, setPlanTicketRequest] = useState<AgvanceAPI.IBlendPlanRequest>({
        agvanceLocationId: null,
        agvanceCropChemistry: agvanceChemistry,
        agvanceCrop: agvanceCrop,
        customerGuid: customerGuid.current,
        fieldGuid: null,
        isSingle: isSinglePlanOrTicket,
        products: [],
        productSetDescription: null,
        quoteStatus: AgvanceAPI.PricingOptionsEnum.NOT_QUOTED,
        recGeneralGuid: null,
    } as AgvanceAPI.IBlendPlanRequest);

    const {
        data: productList,
        isLoading: productLoading,
        error: productError,
    } = AgvanceAPI.useGetProductListQuery(
        {
            customerGuid: props.recSummary.customerGuid,
            agvanceLocationId: planTicketRequest.agvanceLocationId,
        },
        {
            skip: !props.recSummary.customerGuid || !planTicketRequest.agvanceLocationId,
            refetchOnMountOrArgChange: true,
        }
    );
    const {
        data: productSetsList,
        isLoading: productSetsLoading,
        error: productSetsError,
    } = AgvanceAPI.useGetProductSetsQuery(
        {
            customerGuid: props.recSummary.customerGuid,
            agvanceLocationId: planTicketRequest.agvanceLocationId,
        },
        {
            skip: !props.recSummary.customerGuid || !planTicketRequest.agvanceLocationId,
            refetchOnMountOrArgChange: true,
        }
    );
    const {
        data: agvanceCrops,
        isLoading: agvanceCropsLoading,
        error: agvanceCropsError,
    } = AgvanceAPI.useGetAgvanceCropsQuery(
        {
            customerGuid: props.recSummary.customerGuid,
            cropGuid: props.recSummary.cropGuids?.[0],
        },
        { skip: !props.recSummary.customerGuid }
    );
    const {
        data: agvanceCropChemistryList,
        isLoading: agvanceCropChemistryLoading,
        error: agvanceCropChemistryError,
    } = AgvanceAPI.useGetAgvanceCropChemistryQuery(
        { customerGuid: props.recSummary.customerGuid },
        { skip: !props.recSummary.customerGuid }
    );
    const [selectedTab, setSelectedTab] = useState<number>(0);

    const statusMessage = useRef<string>(null);
    const warningMessage = useRef<string>(null);
    const isMultiProduct = useRef<boolean>(true);
    const isAgvancePlan = useRef<boolean>(false);
    const includePreferenceSet = useRef<boolean>(false);

    const { data: blendPlanPreferences } = AgvanceAPI.useGetBlendPlanPreferencesQuery(
        {
            agvanceLocationId: planTicketRequest.agvanceLocationId,
            customerGuid: planTicketRequest.customerGuid,
            preferenceType: isAgvancePlan.current
                ? AgvanceAPI.PreferenceTypeEnum.PLAN
                : AgvanceAPI.PreferenceTypeEnum.BLEND,
        },
        {
            skip: planTicketRequest.agvanceLocationId == null,
            refetchOnMountOrArgChange: true,
        }
    );

    const { data: targetPestsData } = AgvanceAPI.useGetBlendPlanPestsQuery(
        {
            customerGuid: planTicketRequest.customerGuid,
        },
        {
            skip: planTicketRequest.customerGuid == null && !showTargetPests,
            refetchOnMountOrArgChange: true,
        }
    );

    const { data: agvanceFieldList } = AgvanceAPI.useGetAgvanceFieldListQuery(
        {
            customerGuid: props.recSummary.customerGuid,
            fieldGuid: planTicketRequest.fieldGuid,
            linkStatus: 2,
            userGuid: props.userGuid,
        },
        { skip: !props.recSummary.customerGuid || !planTicketRequest.fieldGuid }
    );

    const agvanceGrowerGuids = useMemo(() => {
        const field = agvanceFieldList?.find((f) => f.mappingGuid === planTicketRequest.fieldGuid);
        if (!field) {
            return [];
        }
        return [
            ...new Set(
                [
                    field.agvanceGrowerGuid,
                    ...(field.billingSplits ?? []).map((s) => s.splitCustomerGuid),
                ].filter(Boolean)
            ),
        ];
    }, [agvanceFieldList, planTicketRequest.fieldGuid]);

    const fieldState = useMemo(() => {
        const field = agvanceFieldList?.find((f) => f.mappingGuid === planTicketRequest.fieldGuid);
        return field?.state ?? null;
    }, [agvanceFieldList, planTicketRequest.fieldGuid]);

    const { data: customerPermits, isFetching: customerPermitsFetching } =
        AgvanceAPI.useGetCustomerPermitsQuery(
            {
                customerGuid: props.recSummary.customerGuid,
                agvanceGrowerGuids,
            },
            {
                skip: !props.recSummary.customerGuid || agvanceGrowerGuids.length === 0,
                refetchOnMountOrArgChange: true,
            }
        );

    const pricingOptions: IOption[] = [
        {
            label: "Not Quoted",
            value: AgvanceAPI.PricingOptionsEnum.NOT_QUOTED,
        },
        {
            label: AgvanceAPI.PricingOptionsEnum.QUOTED,
            value: AgvanceAPI.PricingOptionsEnum.QUOTED,
        },
    ];
    if (isAgvancePlan.current) {
        pricingOptions.push({
            label: AgvanceAPI.PricingOptionsEnum.PRICE_OFFERED,
            value: AgvanceAPI.PricingOptionsEnum.PRICE_OFFERED,
        });
    }

    const { loginUserPreferences, isLoading: isLoginUserPreferencesLoading } =
        useLoginUserPreferences(SEND_RECS, [DROPDOWN_DATA], { refetchOnMountOrArgChange: true });

    const { savePreference } = useUpdateLoginUserPreferences(SEND_RECS);

    const [sendPlan, planState] = AgvanceAPI.useSendPlanToAgvanceMutation();
    const [sendBlend, blendState] = AgvanceAPI.useSendBlendTicketToAgvanceMutation();
    const [validateBlend, blendValidationState] = AgvanceAPI.useValidateBlendTicketMutation();
    const [validatePlan, planValidationState] = AgvanceAPI.useValidatePlanMutation();
    const [validatePassword, validatePasswordState] = SkyAPI.useValidatePasswordMutation();

    const generateOptions = (
        list: any[],
        labelKey: string,
        valueKey: string,
        locationOption = false,
        setFunction = null
    ): IOption[] => {
        if (list && list.length > 0) {
            const options = locationOption
                ? list.map((location) => {
                      return location.agvanceLocationId === "(NONE)"
                          ? { label: location.name, value: location.agvanceLocationId }
                          : {
                                label: `${location.agvanceLocationId} - ${location.name}`,
                                value: location.agvanceLocationId,
                            };
                  })
                : list.map((item) => {
                      return { label: item[labelKey], value: item[valueKey] };
                  });

            if (setFunction) {
                setFunction(options);
            }
            return options;
        }
        return [];
    };

    const updateProductDetails = (
        product: AgvanceAPI.IRecProduct,
        pDetails?: AgvanceAPI.IProduct
    ) => {
        const matchedProducts = productList?.filter((pl) => pl.mappingGuid === product.productGuid);
        const productDetails: AgvanceAPI.IProduct =
            pDetails != null
                ? pDetails
                : matchedProducts.length === 1
                ? productList?.find((pl) => pl.mappingGuid === product.productGuid)
                : null;

        if (productDetails) {
            return {
                ...product,
                agvanceGuid: productDetails.agvanceGuid,
                agvancePhysicalState: productDetails.dryOrLiquid,
                billingUomMappingGuid: productDetails.billingUnitMappingGuid,
                billingUOM: productDetails.billingUnit,
                blendUOM: productDetails.blendUnit,
                blendUnitMappingGuid: productDetails.blendUnitMappingGuid,
                density: productDetails.density,
                departmentId: productDetails.deptId,
                inventoryUnit: productDetails.inventoryUnit,
                mappingGuid: productDetails.mappingGuid,
                packageUnit: productDetails.packageUnit,
                productId: productDetails.prodId,
                ratingUOM: productDetails.rateUnit,
                ratingUomMappingGuid: productDetails.rateUnitMappingGuid,
                productClassifications: productDetails.productClassifications,
                federallyRestricted: productDetails.federallyRestricted,
                recommendedRate: productDetails.recommendedRate,
                restrictedStates: productDetails.restrictedStates,
                statesRestrictedForDistribution: productDetails.statesRestrictedForDistribution,
            };
        } else {
            return product;
        }
    };

    const locationsOptions = useMemo(
        () => (locationList ? generateOptions(locationList, null, null, true) : []),
        [locationList]
    );

    const productSetsOptions = useMemo(
        () =>
            productSetsList
                ? generateOptions(productSetsList, "setDescription", "setDescription")
                : [],
        [productSetsList]
    );

    const cropChemistryOptions = useMemo(
        () =>
            agvanceCropChemistryList
                ? generateOptions(agvanceCropChemistryList, "name", "uniqueId")
                : [],
        [agvanceCropChemistryList]
    );

    const cropOptions = useMemo(
        () => (agvanceCrops ? generateOptions(agvanceCrops, "description", "mappingGuid") : []),
        [agvanceCrops]
    );

    const targetPestsOptions = useMemo(
        () => (targetPestsList ? generateOptions(targetPestsList, "pestName", "pestName") : []),
        [targetPestsList]
    );

    const federalPermitOptions = useMemo(
        () =>
            (customerPermits ?? []).flatMap((c) =>
                (c.federalPermits ?? []).map((p) => ({ label: p.permit, value: p.permit }))
            ),
        [customerPermits]
    );
    const statePermitOptions = useMemo(
        () =>
            (customerPermits ?? []).flatMap((c) =>
                (c.statePermits ?? []).map((p) => ({ label: p.permit, value: p.permit }))
            ),
        [customerPermits]
    );
    const federalPermitCustomerByPermit = useMemo(() => {
        const map: Record<string, string> = {};
        (customerPermits ?? []).forEach((c) =>
            (c.federalPermits ?? []).forEach((p) => {
                map[p.permit] = p.customerId;
            })
        );
        return map;
    }, [customerPermits]);

    useEffect(() => {
        if (!customerPermitsFetching && showFederalPermits && federalPermitOptions.length === 1) {
            setSelectedFederalPermit((prev) =>
                prev == null ? federalPermitOptions[0].value : prev
            );
        }
    }, [customerPermitsFetching, showFederalPermits, federalPermitOptions]);

    useEffect(() => {
        if (!customerPermitsFetching && showStatePermits && statePermitOptions.length === 1) {
            setSelectedStatePermits((prev) =>
                prev.length === 0 ? [statePermitOptions[0].value] : prev
            );
        }
    }, [customerPermitsFetching, showStatePermits, statePermitOptions]);

    useEffect(() => {
        setSelectedFederalPermit((prev) =>
            prev != null && !federalPermitOptions.some((o) => o.value === prev) ? null : prev
        );
    }, [federalPermitOptions]);

    useEffect(() => {
        setSelectedStatePermits((prev) => {
            const filtered = prev.filter((permit) =>
                statePermitOptions.some((o) => o.value === permit)
            );
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [statePermitOptions]);

    useEffect(() => {
        if (AgvanceUtils.isAgvanceConnected(personalityId) && props.recSummary) {
            customerGuid.current = props.recSummary.customerGuid;
            isAgvancePlan.current = props.agvanceType === AgvanceType.Plan;
            if (locationList.length === 0) {
                dispatch(
                    fieldActions.fetchAgvanceLocationsList({
                        customerGuid: props.recSummary.customerGuid,
                        agvanceFieldOrgLevelGuid: null,
                    })
                );
            }
        }
        if (props.recSummary?.recGeneralGuid) {
            dispatch(fetchNonMapRecDetails(props.recSummary.recGeneralGuid));
        }
        return () => {
            dispatch(resetSend());
            dispatch(clearRecDetails());
            dispatch(resetApiResult());
        };
    }, []);

    useEffect(() => {
        if (recGeneralDetails) {
            setPlanTicketRequest({
                ...planTicketRequest,
                customerGuid: recGeneralDetails.customerGuid,
                fieldGuid: recGeneralDetails.fieldGuid,
                recGeneralGuid: recGeneralDetails.recGeneralGuid,
            });

            const blendTypeList = new Set<string>();
            const isEquation =
                recGeneralDetails.recType === models.REC_TYPE_NAME_EQUATION_APPLICATION ||
                recGeneralDetails.recType === models.REC_TYPE_NAME_EQUATION_PLANTING;

            const setProductDetails = (
                p: RecNutrientProduct | ProductMixProduct,
                index: number,
                mixGroupId: string
            ) => {
                const costUnit = blending.getPriceUnit(p.costUnitGuid, {
                    productBlendPicklists,
                });

                const product = getEmptyProduct();
                product.mappingProductName = p.productName;
                product.productGuid = p.productGuid;
                product.costUnitGuid = p.costUnitGuid;
                product.listPriceDisplay =
                    p.cost && Number(p.cost)
                        ? `${Number(p.cost).toFixed(2)} ${costUnit.label}`
                        : null;
                product.listPrice = p.cost ? Number(p.cost) : null;
                product.mixGroup = index + 1;
                product.mixGroupId = mixGroupId;

                if (productList?.length > 0) {
                    return updateProductDetails(product);
                }
                return product;
            };

            if (productMixProdList.length === 0 && allProductsInTable.length === 0) {
                const prodMixes: ITabInfo[] = [];
                const distinctProds: AgvanceAPI.IRecProduct[] = [];
                if (isEquation) {
                    recGeneralDetails?.recAreaList
                        .filter((ra) => ra.applyRecToArea)
                        .forEach((recArea) => {
                            const existingNutrientNames = prodMixes.map((pm) => pm.prodMixId);
                            recArea.recs[0].recNutrientList.map((pm, i) => {
                                blendTypeList.add(pm.recNutrientProductMix.physicalState);
                                if (
                                    !existingNutrientNames.includes(
                                        pm.recNutrientProductMix.guaranteedAnalysis
                                    )
                                ) {
                                    const nutrientProducts = pm.recNutrientProductMix.products.map(
                                        (p) =>
                                            setProductDetails(
                                                p,
                                                i,
                                                pm.recNutrientProductMix.guaranteedAnalysis
                                            )
                                    );
                                    distinctProds.push(...nutrientProducts);
                                    prodMixes.push({
                                        include: true,
                                        prodMixId: pm.recNutrientProductMix.guaranteedAnalysis,
                                        prodMixName: pm.nutrientName,
                                        products: nutrientProducts,
                                        tabLabel: NutrientName[pm.nutrientName]
                                            ? NutrientName[pm.nutrientName]
                                            : pm.nutrientName,
                                    });
                                }
                            });
                        });
                } else {
                    recGeneralDetails?.recAreaList.forEach((recArea) => {
                        const existingProdMixIds = prodMixes
                            .filter(Boolean)
                            .map((pm) => pm.prodMixId);
                        recArea.recs[0].productMixList.forEach((pm, i) => {
                            pm.products.map((p) => blendTypeList.add(p.physicalState));
                            if (!existingProdMixIds.includes(pm.productMixId)) {
                                const products = pm.products.map((p) =>
                                    setProductDetails(p, i, pm.productMixId)
                                );
                                distinctProds.push(...products);
                                prodMixes.push({
                                    include: true,
                                    prodMixId: pm.productMixId,
                                    prodMixName: pm.name,
                                    products: products,
                                    tabLabel: pm.name,
                                });
                            }
                            ``;
                        });
                    });
                }
                isMultiProduct.current = prodMixes.filter(Boolean).length > 1;
                setIsSinglePlanOrTicket(isMultiProduct.current ? isSinglePlanOrTicket : true);
                blendSetType.current =
                    blendTypeList.size == 1 ? blendTypeList.values().next().value : null;
                setProductMixProdList(prodMixes);
                setAllProductsInTable(distinctProds);
            }
        }
    }, [recGeneralDetails]);

    useEffect(() => {
        if (
            productList?.length > 0 &&
            (allProductsInTable.length > 0 || productMixProdList.length > 0)
        ) {
            const newProducts = [];
            allProductsInTable.forEach((p) => {
                newProducts.push(updateProductDetails(p));
            });
            const newProductMixList = [...productMixProdList];
            newProductMixList.forEach((pm) => {
                const updatedProducts = [];
                pm.products.forEach((p) => {
                    updatedProducts.push(updateProductDetails(p));
                });
                pm.products = updatedProducts;
            });
            setAllProductsInTable(newProducts);
            setProductMixProdList(newProductMixList);
        }
    }, [productList]);

    useEffect(() => {
        setIsLoading(
            isRecLoading ||
                agvanceCropsLoading ||
                agvanceCropChemistryLoading ||
                productLoading ||
                productSetsLoading ||
                isLoginUserPreferencesLoading
        );
    }, [
        isRecLoading,
        agvanceCropsLoading,
        agvanceCropChemistryLoading,
        productLoading,
        productSetsLoading,
        isLoginUserPreferencesLoading,
    ]);

    useEffect(() => {
        setIsLoading(false);
        if (productError) {
            updateStatusMessage(
                formatString(StatusMessage.ERROR, `Error loading Products: ${productError}`)
            );
        }
        if (productSetsError) {
            updateStatusMessage(
                formatString(StatusMessage.ERROR, `Error loading Product Sets: ${productSetsError}`)
            );
        }
        if (agvanceCropsError) {
            updateStatusMessage(
                formatString(
                    StatusMessage.ERROR,
                    `Error loading Agvance Crops: ${agvanceCropsError}`
                )
            );
        }
        if (agvanceCropChemistryError) {
            updateStatusMessage(
                formatString(
                    StatusMessage.ERROR,
                    `Error loading Agvance Crop Chemistry: ${agvanceCropChemistryError}`
                )
            );
        }
    }, [productError, productSetsError, agvanceCropsError, agvanceCropChemistryError]);

    useEffect(() => {
        if (loginUserPreferences?.dropdownData && !isLoginUserPreferencesLoading) {
            try {
                const newPlanOrTicket = {
                    ...planTicketRequest,
                } as unknown as AgvanceAPI.IBlendPlanRequest;
                const dropdownData = JSON.parse(loginUserPreferences.dropdownData);

                if (dropdownData.customApplied != null) {
                    setCustomApplied(dropdownData.customApplied);
                }

                if (dropdownData.nutrientsInclude) {
                    nutrientsIncludePreference.current = dropdownData.nutrientsInclude;
                }

                if (newPlanOrTicket.agvanceLocationId == null) {
                    setIsSinglePlanOrTicket(dropdownData.single);
                    newPlanOrTicket.agvanceLocationId = dropdownData.location
                        ? dropdownData.location
                        : null;
                }

                if (productSetsList?.length > 0) {
                    const dropdownData = JSON.parse(loginUserPreferences.dropdownData);
                    newPlanOrTicket.productSetDescription =
                        dropdownData.productSet &&
                        productSetsList?.length &&
                        productSetsList?.find((ps) => ps.setDescription === dropdownData.productSet)
                            ? dropdownData.productSet
                            : null;
                }
                if (agvanceCrops?.length > 0 && props.recSummary?.cropGuids?.length > 0) {
                    const matchedCrop = agvanceCrops.find(
                        (c) => c.mappingGuid === props.recSummary.cropGuids[0]
                    );
                    setAgvanceCrop(matchedCrop ? matchedCrop.mappingGuid.toString() : null);
                    newPlanOrTicket.agvanceCrop = matchedCrop
                        ? matchedCrop.description.toString()
                        : null;
                }

                setPlanTicketRequest(newPlanOrTicket);
            } catch (error) {
                console.error("Error parsing dropDownData:", error);
            }
        }
    }, [loginUserPreferences, productSetsList, agvanceCrops]);

    useEffect(() => {
        let noMatchedProducts = true;
        const missingAgvanceParams =
            planTicketRequest.agvanceLocationId === null ||
            planTicketRequest.productSetDescription === null;

        if (isSinglePlanOrTicket) {
            noMatchedProducts = allProductsInTable.some((p) => p.productId === null);
        } else {
            const productMixesToInclude = productMixProdList.filter((pm) => pm.include);
            if (productMixesToInclude.length > 0) {
                noMatchedProducts = productMixesToInclude.some((pm) =>
                    pm.products.some((p) => p.productId == null)
                );
            }
        }
        setDisableSend(noMatchedProducts || missingAgvanceParams);
    }, [
        planTicketRequest.agvanceLocationId,
        planTicketRequest.productSetDescription,
        allProductsInTable,
        productMixProdList,
        isSinglePlanOrTicket,
    ]);

    useEffect(() => {
        if (productMixProdList.length > 0 && !includePreferenceSet.current) {
            const prodMixList = [...productMixProdList];
            prodMixList.forEach((pm) => {
                if (nutrientsIncludePreference.current[pm.prodMixId]) {
                    pm.include = nutrientsIncludePreference.current[pm.prodMixId];
                } else {
                    pm.include = false;
                }
            });
            includePreferenceSet.current = true;
            setProductMixProdList([...prodMixList]);
        }
    }, [nutrientsIncludePreference, productMixProdList]);

    useEffect(() => {
        const blendResult = blendResults[blendResults.length - 1];
        if (blendResult) {
            if (blendResult.errorList == null || blendResult.errorList.length === 0) {
                const message = `${
                    isSinglePlanOrTicket ? "" : `${blendResult.productMixName} - `
                } #${blendResult.ticketNumber}${
                    blendResult.warningList && blendResult.warningList.length > 0
                        ? `\n${StatusMessage.WARNING}\n\t${blendResult.warningList.join("\n\t")}`
                        : ""
                }`;
                updateStatusMessage(formatString(StatusMessage.SUCCESS, "Blend", message));
            } else {
                updateStatusMessage(
                    formatString(StatusMessage.ERROR, blendResult.errorList.join("\n"))
                );
                setDisableSend(false);
            }
        }
    }, [blendResults]);

    useEffect(() => {
        const planResult = planResults[planResults.length - 1];
        if (planResult) {
            if (planResult.errorList == null || planResult.errorList.length === 0) {
                const message = `${
                    isSinglePlanOrTicket ? "" : `${planResult.productMixName} - `
                }Plan ${planResult.planId}`;
                updateStatusMessage(formatString(StatusMessage.SUCCESS, "Plan", message));
            } else {
                updateStatusMessage(
                    formatString(StatusMessage.ERROR, planResult.errorList.join("\n"))
                );
                setDisableSend(false);
            }
        }
    }, [planResults]);

    useEffect(() => {
        if (recStatus && recStatus.updateRecStatus) {
            setSuccessfulBlendPlan(true);
        }
    }, [recStatus]);

    useEffect(() => {
        if (blendPlanPreferences) {
            setAgvancePreferences(blendPlanPreferences);
            const requireDateValues = ["Warning Only", "Disallow Save"];
            const startPref = blendPlanPreferences.find(
                (p) => p.name === "RequireFieldPlanStartDate"
            );
            const endPref = blendPlanPreferences.find((p) => p.name === "RequireFieldPlanEndDate");
            setShowStartDate(requireDateValues.includes(startPref?.value));
            setShowEndDate(requireDateValues.includes(endPref?.value));
        }
    }, [blendPlanPreferences]);

    useEffect(() => {
        if (!blendPlanPreferences) {
            return;
        }
        const requireDateValues = ["Warning Only", "Disallow Save"];
        const targetPestsRequired = blendPlanPreferences.find(
            (p) => p.name === "RequireTargetPest"
        );
        const requiresByPref = requireDateValues.includes(targetPestsRequired?.value);
        const hasFederallyRestrictedProduct = isSinglePlanOrTicket
            ? allProductsInTable.some((p) => p?.federallyRestricted)
            : productMixProdList.some((mix) => mix.products?.some((p) => p?.federallyRestricted));
        setShowTargetPests(requiresByPref && hasFederallyRestrictedProduct);
    }, [blendPlanPreferences, isSinglePlanOrTicket, allProductsInTable, productMixProdList]);

    useEffect(() => {
        if (!blendPlanPreferences) {
            return;
        }
        const checking = blendPlanPreferences.find(
            (p) => p.name === "FederalorStateChecking"
        )?.value;
        const products = isSinglePlanOrTicket
            ? allProductsInTable
            : productMixProdList.flatMap((mix) => mix.products ?? []);
        const hasFederallyRestrictedProduct = products.some((p) => p?.federallyRestricted);
        const hasStateRestrictedProduct = products.some((p) =>
            (p?.restrictedStates ?? []).some(
                (s) => !fieldState || s.toLowerCase() === fieldState.toLowerCase()
            )
        );

        const federalOn = (checking === "Federal" || checking === "Both") && !customApplied;
        const stateOn = (checking === "State" || checking === "Both") && !customApplied;

        setShowFederalPermits(federalOn && hasFederallyRestrictedProduct);
        setShowStatePermits(stateOn && hasStateRestrictedProduct);
    }, [
        blendPlanPreferences,
        isSinglePlanOrTicket,
        allProductsInTable,
        productMixProdList,
        customApplied,
        fieldState,
    ]);

    useEffect(() => {
        if (targetPestsData?.length > 0) {
            setTargetPestsList(targetPestsData);
        }
    }, [targetPestsData]);

    const getEmptyProduct = (): AgvanceAPI.IRecProduct => {
        return {
            agvanceGuid: null,
            agvancePhysicalState: null,
            billingUOM: null,
            billingUomMappingGuid: null,
            blendUOM: null,
            blendUnitMappingGuid: null,
            costUnitGuid: null,
            departmentId: null,
            inventoryUnit: null,
            listPrice: null,
            listPriceDisplay: null,
            mappingGuid: null,
            mappingProductName: null,
            mixGroup: 0,
            mixGroupId: null,
            productGuid: null,
            packageUnit: null,
            productId: null,
            ratingUOM: null,
            ratingUomMappingGuid: null,
        } as unknown as AgvanceAPI.IRecProduct;
    };

    const handleSavePreference = async () => {
        const dropdownData = {
            location: planTicketRequest.agvanceLocationId,
            productSet: planTicketRequest.productSetDescription,
            single: isSinglePlanOrTicket,
            nutrientsInclude: productMixProdList.reduce<Record<string, boolean>>((acc, item) => {
                acc[item.prodMixId] = item.include;
                return acc;
            }, {}),
            customApplied,
        };
        await savePreference(DROPDOWN_DATA, JSON.stringify(dropdownData));
    };

    const updateStatusMessage = (latestMessage: string, tabs?: number) =>
        appendLine(statusMessage, latestMessage, tabs);
    const updateWarningMessage = (latestMessage: string, tabs?: number) =>
        appendLine(warningMessage, latestMessage, tabs);

    const formatString = (template: string, ...args: any[]): string => {
        return template.replace(/{(\d+)}/g, (match, index) => {
            return typeof args[index] !== "undefined" ? args[index] : match;
        });
    };

    const sendRecToAgvance = async (requestList: AgvanceAPI.IBlendPlanRequest[]) => {
        if (!requestList.length) {
            return;
        }
        // Resets the status message for each click. Can have it rolling.
        statusMessage.current = StatusMessage.REQUEST_SENT;
        warningMessage.current = null;

        try {
            const result = isAgvancePlan.current
                ? await validatePlan(requestList).unwrap()
                : await validateBlend(requestList).unwrap();

            stopWithPassword.current = result?.stopWithPasswordMessages?.length > 0;

            if (
                result?.errors?.length > 0 ||
                result?.warnings?.length > 0 ||
                result?.stopWithPasswordMessages?.length > 0
            ) {
                updateStatusMessage(StatusMessage.VALIDATION);
                if (result?.errors.length > 0) {
                    updateStatusMessage(StatusMessage.ERRORS, 1);
                    result.errors.forEach((error) => {
                        updateStatusMessage(error, 2);
                    });
                    setDisableSend(false);
                }
                if (result?.warnings.length > 0) {
                    updateStatusMessage(StatusMessage.WARNING, 1);
                    result.warnings.forEach((warning) => {
                        updateStatusMessage(warning, 2);
                        updateWarningMessage(warning);
                    });
                }
                if (result?.stopWithPasswordMessages.length > 0) {
                    updateStatusMessage(StatusMessage.WARNING, 1);
                    result.stopWithPasswordMessages.forEach((passwordMessage) => {
                        updateStatusMessage(passwordMessage, 2);
                        updateWarningMessage(passwordMessage);
                    });
                    setShowValidationWarningDialog(true);
                }

                if (result?.errors.length === 0) {
                    setShowValidationWarningDialog(true);
                }
                setValidatedRequestKey(result.key);
            }

            setIsLoading(false);
        } catch (err) {
            updateStatusMessage(StatusMessage.REQUEST_FAILED);
            if (err.data?.errors?.length > 0) {
                updateStatusMessage(
                    `${err.status}:  ${err.data?.errors?.map((e) => e.message).join("\n")}`,
                    1
                );
            } else {
                updateStatusMessage(
                    `${err.status}: ${err.data?.message || "An unknown error occurred."}`,
                    1
                );
            }
            setDisableSend(false);
            setIsLoading(false);
        }
    };

    const onBlendPlanSend = () => {
        setIsLoading(true);
        setDisableSend(true);

        const requests: AgvanceAPI.IBlendPlanRequest[] = [];
        const allValidProducts = isSinglePlanOrTicket
            ? allProductsInTable.every(
                  (p) => p.blendUnitMappingGuid && p.ratingUomMappingGuid && p.billingUomMappingGuid
              )
            : productMixProdList
                  .filter((pm) => pm.include)
                  .every((pm) =>
                      pm.products.every(
                          (p) =>
                              p.blendUnitMappingGuid &&
                              p.ratingUomMappingGuid &&
                              p.billingUomMappingGuid
                      )
                  );
        // Save preferences
        handleSavePreference();
        planTicketRequest.isSingle = isSinglePlanOrTicket;
        if (isSinglePlanOrTicket) {
            // Call with single plan/ticket products
            planTicketRequest.products = allProductsInTable;
            if (planTicketRequest.quoteStatus === AgvanceAPI.PricingOptionsEnum.NOT_QUOTED) {
                allProductsInTable.forEach((p) => (p.listPrice = null));
            }
            if (allValidProducts) {
                requests.push(planTicketRequest);
            }
        } else {
            // Call with product mix products
            requests.push(
                ...productMixProdList
                    .filter((pm) => pm.include)
                    .map((pm) => {
                        if (
                            planTicketRequest.quoteStatus ===
                            AgvanceAPI.PricingOptionsEnum.NOT_QUOTED
                        ) {
                            pm.products.forEach((p) => (p.listPrice = null));
                        }
                        if (allValidProducts) {
                            const request: AgvanceAPI.IBlendPlanRequest = JSON.parse(
                                JSON.stringify(planTicketRequest)
                            );
                            request.products = pm.products;
                            request.productMixId = pm.prodMixId;
                            request.productMixName = pm.prodMixName;
                            return request;
                        }
                    })
                    .filter(Boolean)
            );
        }
        sendRecToAgvance(
            requests.map(
                (r) =>
                    ({
                        ...r,
                        blendPlanPreferences: agvancePreferences,
                        ...(isAgvancePlan.current && {
                            startDate: planStartDate ? moment(planStartDate).toDate() : null,
                            endDate: planEndDate ? moment(planEndDate).toDate() : null,
                        }),
                        customApplied,
                        targetPests,
                        federalPermits: selectedFederalPermit
                            ? [
                                  {
                                      permit: selectedFederalPermit,
                                      customerId:
                                          federalPermitCustomerByPermit[selectedFederalPermit],
                                  },
                              ]
                            : [],
                        statePermits: selectedStatePermits.map((permit) => ({ permit })),
                    } as AgvanceAPI.IBlendPlanRequest)
            )
        );
        if (!allValidProducts) {
            setShowWarningDialog(true);
        }
    };

    const onValidatedBlendPlanSend = async () => {
        updateStatusMessage(StatusMessage.REQUEST_SENT);
        try {
            closeValidationWarning();
            isAgvancePlan.current
                ? await sendPlan([{ key: validatedRequestKey }]).unwrap()
                : await sendBlend([{ key: validatedRequestKey }]).unwrap();

            setIsLoading(false);
        } catch (err) {
            updateStatusMessage(StatusMessage.REQUEST_FAILED);
            if (err.data?.errors?.length > 0) {
                updateStatusMessage(
                    `${err.status}:  ${err.data?.errors?.map((e) => e.message).join("\n")}`,
                    1
                );
            } else {
                updateStatusMessage(
                    `${err.status}: ${err.data?.message || "An unknown error occurred."}`,
                    1
                );
            }
            setDisableSend(false);
            setIsLoading(false);
        }
    };

    const onValidatePassWord = async () => {
        updateStatusMessage(StatusMessage.VALIDATE_PASSWORD);
        try {
            const result = await validatePassword({
                password: btoa(creditPassword),
            }).unwrap();

            if (result.success) {
                setPasswordError(null);
                closeValidationWarning();
                closeValidationPassword();
                updateStatusMessage(StatusMessage.REQUEST_SENT);
                isAgvancePlan.current
                    ? await sendPlan([{ key: validatedRequestKey }]).unwrap()
                    : await sendBlend([{ key: validatedRequestKey }]).unwrap();
                setIsLoading(false);
            } else {
                setPasswordError(result.message ?? "Invalid password. Please try again.");
                setIsLoading(false);
            }
        } catch (err) {
            setPasswordError(err.data?.message ?? "Invalid Password. Please try again.");
            setIsLoading(false);
        }
    };

    const onProductChange = (value, index) => {
        const productDetails = productList.find((p) => p.prodId === value);
        if (isSinglePlanOrTicket) {
            // Update in allProducts
            const product = allProductsInTable[index];
            if (product) {
                allProductsInTable[index] = updateProductDetails(product, productDetails);
                setAllProductsInTable([...allProductsInTable]);
            }
        } else {
            // Update in productMix
            const updateProductMix = productMixProdList[selectedTab];
            if (updateProductMix?.products[index]) {
                updateProductMix.products[index] = updateProductDetails(
                    updateProductMix.products[index],
                    productDetails
                );
                setProductMixProdList([...productMixProdList]);
            }
        }
    };

    const openPasswordModal = () => {
        setShowValidationWarningDialog(false);
        setShowValidationPasswordDialog(true);
    };

    const getHeader = () => {
        const showPricing =
            planTicketRequest.quoteStatus != AgvanceAPI.PricingOptionsEnum.NOT_QUOTED;
        return (
            <div className="product-header" key="header">
                {isSinglePlanOrTicket && isMultiProduct.current && (
                    <div className="product-column xsmall">{formatMessage(messages.mixId)}</div>
                )}
                <div className="product-column xxlarge">{formatMessage(messages.products)}</div>
                <div className="product-column agvance-products xxxlarge">
                    {formatMessage(messages.agvanceProducts)}
                </div>
                <div className="product-column xsmall">{formatMessage(messages.deptId)}</div>
                <div className="product-column xsmall">{formatMessage(messages.invUnit)}</div>
                <div className="product-column xsmall">{formatMessage(messages.pkgUnit)}</div>
                <div className="product-column xsmall">{formatMessage(messages.bldUnit)}</div>
                {showPricing && (
                    <div className={classNames("product-column", "small")}>
                        {formatMessage(messages.pricing)}
                    </div>
                )}
            </div>
        );
    };

    const generateRows = (products: AgvanceAPI.IRecProduct[], key: any) => {
        const showPricing =
            planTicketRequest.quoteStatus != AgvanceAPI.PricingOptionsEnum.NOT_QUOTED;
        return products.map((product, i) => {
            const options = generateOptions(
                productList?.filter((p) => p.mappingGuid === product.productGuid),
                "name",
                "prodId"
            );
            return (
                <div className="product-row" key={`product-combined-${key}-${i}`}>
                    {isSinglePlanOrTicket && isMultiProduct.current && (
                        <div className="product-column xsmall">{product.mixGroup}</div>
                    )}
                    <div className="product-column xxlarge">{product.mappingProductName}</div>
                    <div className="product-column agvance-products xxxlarge">
                        <SelectInput
                            disabled={successfulBlendPlan || isLoading}
                            reactKey={`select-input-${key}-${i}`}
                            initialFilterStr={null}
                            value={product.productId || (options.length === 1 && options[0].value)}
                            clearable={false}
                            options={options}
                            onChange={(value) => onProductChange(value, i)}
                            showTopLabel={false}
                        />
                    </div>
                    <div className={classNames("product-column", "xsmall")}>
                        {product.departmentId}
                    </div>
                    <div className={classNames("product-column", "xsmall")}>
                        {product.inventoryUnit}
                    </div>
                    <div className={classNames("product-column", "xsmall")}>
                        {product.packageUnit}
                    </div>
                    <div className={classNames("product-column", "xsmall")}>{product.blendUOM}</div>
                    {showPricing && (
                        <div className={classNames("product-column", "small")}>
                            {product.listPriceDisplay}
                        </div>
                    )}
                </div>
            );
        });
    };

    const updateNutrientIncludePreference = (mixId: string, value: boolean) => {
        productMixProdList.find((pm) => pm.prodMixId === mixId).include = value;
        setProductMixProdList([...productMixProdList]);
    };

    const getPanes = () => {
        const panes = productMixProdList.map((pm, i) => {
            return (
                <Pane key={`${pm.prodMixName}-${i}`} label={pm.tabLabel}>
                    {
                        <Checkbox
                            key={`checkbox-${pm.prodMixName}-${i}`}
                            disabled={successfulBlendPlan || isLoading}
                            label={formatMessage(messages.include)}
                            onChange={(e, value) =>
                                updateNutrientIncludePreference(pm.prodMixId, value)
                            }
                            value={pm.include}
                        />
                    }
                    <div className="product-table">
                        {[getHeader(), ...generateRows(pm.products, i)]}
                    </div>
                </Pane>
            );
        });
        return [...panes];
    };

    const getTabbedProductList = () => {
        return (
            <Tabs
                key={"product-mix-tab"}
                className="blend-type-tabs"
                selected={selectedTab}
                onTabSelect={(idx) => setSelectedTab(idx)}
            >
                {getPanes()}
            </Tabs>
        );
    };

    const getCombinedProductList = () => {
        const productRows = generateRows(allProductsInTable, "combined");
        return (
            <div className="product-table">
                {getHeader()}
                {productRows}
            </div>
        );
    };

    const updateLocation = (value) => {
        // Clear products set in all mixes
        allProductsInTable.forEach((p) => {
            p.agvancePhysicalState = null;
            p.billingUomMappingGuid = null;
            p.billingUOM = null;
            p.blendUOM = null;
            p.blendUnitMappingGuid = null;
            p.departmentId = null;
            p.inventoryUnit = null;
            p.mappingGuid = null;
            p.packageUnit = null;
            p.productId = null;
            p.ratingUOM = null;
            p.ratingUomMappingGuid = null;
        });
        productMixProdList.forEach((pm) => {
            pm.products.forEach((p) => {
                p.agvancePhysicalState = null;
                p.billingUomMappingGuid = null;
                p.billingUOM = null;
                p.blendUOM = null;
                p.blendUnitMappingGuid = null;
                p.departmentId = null;
                p.inventoryUnit = null;
                p.mappingGuid = null;
                p.packageUnit = null;
                p.productId = null;
                p.ratingUOM = null;
                p.ratingUomMappingGuid = null;
            });
        });
        setProductMixProdList([...productMixProdList]);
        setAllProductsInTable([...allProductsInTable]);
        setPlanTicketRequest({
            ...planTicketRequest,
            agvanceLocationId: value,
            productSetDescription: null,
        });
    };

    const updatePlanOrBlend = (key, value) => {
        planTicketRequest[key] = value;
        setPlanTicketRequest({ ...planTicketRequest });
    };

    const updatePlanOrBlendFromList = (
        key,
        value,
        optionsList,
        listKey,
        valueToSet,
        setState = null
    ) => {
        // To set the label insted of value to ticket.
        // For Crop and Chemistry, not sure if Name will be unique, so using another property. API needs Name.
        const optionDetails = optionsList.find((option) => option[listKey] === value);
        const newPlanOrTicket = JSON.parse(
            JSON.stringify(planTicketRequest)
        ) as AgvanceAPI.IBlendPlanRequest;
        newPlanOrTicket[key] = optionDetails ? optionDetails[valueToSet] : null;
        setPlanTicketRequest(newPlanOrTicket);
        if (setState) {
            setState(value);
        }
    };

    const updateSelectedTargetPests = (value) => {
        setTargetPests(value);
    };

    const closeWarning = () => {
        setShowWarningDialog(false);
        setIsLoading(false);
        setDisableSend(false);
    };

    const closeValidationWarning = () => {
        setShowValidationWarningDialog(false);
        setIsLoading(false);
        setDisableSend(false);
        warningMessage.current = null;
    };

    const closeValidationPassword = () => {
        stopWithPassword.current = false;
        setShowValidationPasswordDialog(false);
        setCreditPassword("");
        setPasswordError("");
        setIsLoading(false);
        setDisableSend(false);
    };

    return (
        <React.Fragment>
            <DialogBox
                action="Send"
                actionDisabled={successfulBlendPlan || disableSend || isLoading}
                className={"send-rec-agvance-dialog"}
                draggable
                footerType={DialogBoxFooterType.ACTION_CLOSE}
                forceOverflow
                isOpen={true}
                onAction={onBlendPlanSend}
                onClose={props.onClose}
                title={
                    isAgvancePlan.current
                        ? formatMessage(messages.sendRecToAgvancePlanTitle)
                        : formatMessage(messages.sendRecToAgvanceBlendTicketTitle)
                }
                unrestricted
            >
                {isLoading && <Loader />}
                <div className="agvance-controls-layout">
                    <div className="agvance-inputs-column">
                        <SelectInput
                            required
                            clearable={false}
                            disabled={successfulBlendPlan || isLoading}
                            placeholderText={formatMessage(messages.location)}
                            options={locationsOptions}
                            onChange={(value) => updateLocation(value)}
                            value={planTicketRequest.agvanceLocationId}
                        />
                        <SelectInput
                            required
                            disabled={successfulBlendPlan || isLoading}
                            clearable={false}
                            placeholderText={formatMessage(messages.productSet)}
                            options={productSetsOptions}
                            onChange={(value) => updatePlanOrBlend("productSetDescription", value)}
                            value={planTicketRequest.productSetDescription}
                        />
                        <SelectInput
                            required
                            disabled={successfulBlendPlan || isLoading}
                            clearable={false}
                            placeholderText={formatMessage(messages.pricing)}
                            options={pricingOptions}
                            onChange={(value) => updatePlanOrBlend("quoteStatus", value)}
                            value={planTicketRequest.quoteStatus}
                        />
                        <SelectInput
                            disabled={successfulBlendPlan || isLoading}
                            required={false}
                            options={cropOptions}
                            onChange={(value) =>
                                updatePlanOrBlendFromList(
                                    "agvanceCrop",
                                    value,
                                    agvanceCrops,
                                    "mappingGuid",
                                    "description",
                                    setAgvanceCrop
                                )
                            }
                            placeholderText={formatMessage(messages.agvanceCrop)}
                            value={agvanceCrop}
                        />
                        <SelectInput
                            disabled={successfulBlendPlan || isLoading}
                            required={false}
                            placeholderText={formatMessage(messages.agvanceCropChemistry)}
                            options={cropChemistryOptions}
                            onChange={(value) =>
                                updatePlanOrBlendFromList(
                                    "agvanceCropChemistry",
                                    value,
                                    agvanceCropChemistryList,
                                    "uniqueId",
                                    "name",
                                    setAgvanceChemistry
                                )
                            }
                            value={agvanceChemistry}
                        />
                        {!isAgvancePlan.current && showTargetPests && (
                            <SelectInputMulti
                                isDisabled={successfulBlendPlan || isLoading}
                                placeholder={"Target Pests"}
                                value={targetPests}
                                options={targetPestsOptions}
                                onChange={(value) => updateSelectedTargetPests(value)}
                            />
                        )}
                        {showFederalPermits && (
                            <SelectInput
                                disabled={
                                    successfulBlendPlan ||
                                    isLoading ||
                                    federalPermitOptions.length === 0
                                }
                                placeholderText={"Federal Permit"}
                                options={federalPermitOptions}
                                value={selectedFederalPermit}
                                onChange={(value) => setSelectedFederalPermit(value)}
                            />
                        )}
                        {showStatePermits && (
                            <SelectInputMulti
                                isDisabled={
                                    successfulBlendPlan ||
                                    isLoading ||
                                    statePermitOptions.length === 0
                                }
                                placeholder={"State Permits"}
                                value={selectedStatePermits}
                                options={statePermitOptions}
                                onChange={(value) => setSelectedStatePermits(value)}
                            />
                        )}
                        {isAgvancePlan.current && showStartDate && (
                            <DateInput
                                disabled={successfulBlendPlan || isLoading}
                                placeholderText="Plan Start Date"
                                value={planStartDate}
                                onChange={(value) => setPlanStartDate(value)}
                            />
                        )}
                        {isAgvancePlan.current && showEndDate && (
                            <DateInput
                                disabled={successfulBlendPlan || isLoading}
                                placeholderText="Plan End Date"
                                value={planEndDate}
                                onChange={(value) => setPlanEndDate(value)}
                            />
                        )}
                    </div>
                    <div className="agvance-checkboxes-column">
                        <Checkbox
                            disabled={successfulBlendPlan || isLoading || !isMultiProduct.current}
                            label={
                                isAgvancePlan.current
                                    ? formatMessage(messages.singlePlan)
                                    : formatMessage(messages.singleBlendTicket)
                            }
                            onChange={(e, value) => setIsSinglePlanOrTicket(value)}
                            value={isSinglePlanOrTicket}
                        />
                        <Checkbox
                            label={formatMessage(messages.customApplied)}
                            onChange={(e, value) => setCustomApplied(value)}
                            value={customApplied}
                        />
                    </div>
                </div>
                <div className="product-table-container">
                    {isSinglePlanOrTicket ? getCombinedProductList() : getTabbedProductList()}
                </div>
                <div className="status-message">
                    {(planValidationState.isLoading ||
                        blendValidationState.isLoading ||
                        statusMessage.current) && (
                        <div className="message-box">{`${statusMessage.current}`}</div>
                    )}
                </div>
            </DialogBox>
            <DialogBox
                className={"invalid-products-warning-dialog"}
                title={formatMessage(messages.invalidProductsTitle)}
                footerType={DialogBoxFooterType.NONE}
                isOpen={showWarningDialog}
                onClose={() => closeWarning()}
            >
                {formatMessage(messages.invalidProductsForAgvance)}
            </DialogBox>
            <DialogBox
                action={stopWithPassword.current ? "Enter Password" : "Yes"}
                className={"validation-warning-dialog"}
                draggable
                title={formatMessage(messages.validationWarning)}
                footerType={DialogBoxFooterType.ACTION_NO}
                forceOverflow
                isOpen={showValidationWarningDialog}
                onAction={stopWithPassword.current ? openPasswordModal : onValidatedBlendPlanSend}
                onClose={() => closeValidationWarning()}
            >
                {(planState.isLoading || blendState.isLoading || warningMessage.current) && (
                    <div className="message-box">
                        {`${warningMessage.current}\n${
                            stopWithPassword.current
                                ? "Please enter the password to continue."
                                : "Do you wish to continue?"
                        }`}
                    </div>
                )}
            </DialogBox>
            <DialogBox
                action="Proceed"
                className={"validation-warning-dialog"}
                draggable
                title={formatMessage(messages.validationPassword)}
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                forceOverflow
                isOpen={showValidationPasswordDialog}
                onAction={onValidatePassWord}
                onClose={() => closeValidationPassword()}
            >
                {validatePasswordState.isLoading && <Loader />}
                <div className="message-box">
                    {`This customer has exceeded their credit limit. Enter the password to proceed with the blend.`}
                </div>
                <TextInput
                    password
                    placeholderText="Password"
                    value={creditPassword}
                    onChange={(value) => setCreditPassword(value)}
                />
                {passwordError && <div className="password-error-message">{passwordError}</div>}
            </DialogBox>
        </React.Fragment>
    );
};

export default SendRecAgvance;
